// Copyright ©️ 2026 eVolve MEP, LLC

import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import {
  type CustomProperty,
  type CutList,
  type GetPaginatedOpts,
  type MachineElement,
  useEvolveGet,
  useEvolvePaginatedGet,
  useEvolvePatch,
  useEvolvePost,
} from '@evolvemep/api-ts';
import { Accordion, Badge, Button, Card, Group, LoadingOverlay, Stack, Text } from '@mantine/core';
import { AgGridReact } from 'ag-grid-react';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AccordionControlsWithActions } from 'components/Mantine/AccordionControlsWithActions';
import { InfoIcon } from 'components/Mantine/InfoIcon';
import { VariableTrackingModal } from 'components/Mantine/Modals/VariableTrackingModal';
import { SearchableSelect } from 'components/Mantine/TypeSafeSelect/PaginatedSelectors/SearchableSelect';
import { compactRowHeight } from 'helpers/ag-grid/baseColumnDef';
import { AgGridEditable } from 'helpers/ag-grid/EditableCells/AgGridEditable';
import { refreshGrid } from 'helpers/ag-grid/helpers';
import { useServerSideGrid } from 'helpers/ag-grid/useServerSideGrid';
import { convertInchesToImperialString } from 'helpers/convertInchesToImperialString';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { useHasAccess } from 'hooks/user/useHasAccess';
import { getMachineElementsColumnDef } from 'modules/Shop/EvolveFabrication/columnDefs';
import { useConduits } from 'modules/Shop/EvolveFabrication/useConduits';

type Props = {
  cutList: CutList;
  canMarkItemsAsDone: boolean;
  onCutListEmpty: () => void;
  /** The Fabrication Item custom properties to show as editable columns. */
  customProperties: CustomProperty[];
  /** Extra actions for this cut list, rendered beside its own. */
  actions?: ReactNode;
};

const SmallDetail = ({ label, children }: { label: string; children: string | number | null | undefined }) =>
  isNil(children) ? null : (
    <Group gap={6}>
      <Text c="dimmed">{label}</Text>
      <Text fw={500}>{children}</Text>
    </Group>
  );

export const MachineCutList = ({
  cutList: initialCutList,
  canMarkItemsAsDone,
  onCutListEmpty,
  customProperties,
  actions,
}: Props) => {
  const hasFabricationFullAccess = useHasAccess('fabrication', 'write');
  const [cutList, onCutListUpdated] = useState(initialCutList);
  const { cutListId } = cutList;
  const { conduitTypes, conduitSizes } = useConduits();
  const defaultOpts = useMemo<GetPaginatedOpts<'shop/fabrication/element'>>(
    () => ({
      defaultConfig: {
        params: {
          cutListId,
        },
      },
    }),
    [cutListId],
  );
  const { fetchPage, setDefaultOpts, abortController } = useEvolvePaginatedGet('shop/fabrication/element', defaultOpts);
  useEffect(() => {
    setDefaultOpts(defaultOpts);
  }, [defaultOpts, setDefaultOpts]);

  const [cutToRemove, setCutToRemove] = useState<MachineElement | null>(null);
  const { apiCall: removeElementsApiCall } = useEvolvePost('shop/workOrder/:workOrderId/removeElements');

  const colDef = useMemo(
    () =>
      getMachineElementsColumnDef({
        conduitTypes,
        conduitSizes,
        onRemoveElement: setCutToRemove,
        isWorkOrderPage: true,
        isCutListView: true,
        allowQaModal: false,
        customElementProperties: customProperties,
      }),
    [conduitTypes, conduitSizes, customProperties],
  );
  const { agGridProps, gridApi } = useServerSideGrid({
    tableName: 'cut-list',
    colDef,
    fetchPage,
    abortController,
    rowId: 'elementId',
  });

  const { apiCall: getCutList, loading: loadingCutList } = useEvolveGet(`shop/fabrication/cutList/${cutListId}`);

  const refreshCutList = useCallback(async () => {
    refreshGrid(gridApi);
    await getCutList()
      .then(onCutListUpdated)
      .catch(() => {
        // If removing a cut causes the cut list to be empty,
        // the cut list will be deleted and the refresh will return a 404.
        // We can assume the cut list is empty and call onCutListEmpty.
        // Either way, upstream will refresh every cut list, so any other error will be dealt with as well.
        onCutListEmpty();
      });
  }, [getCutList, onCutListUpdated, gridApi, onCutListEmpty]);

  const paginatedGetCutProfile = useEvolvePaginatedGet('shop/fabrication/cutProfile');
  const { apiCall: assignCutProfile, loading: assigning } = useEvolvePatch(
    'shop/fabrication/cutList/:cutListId/change-cut-profile/:cutProfileId',
  );

  const { apiCall: doCutList, loading: doing } = useEvolvePost(`shop/fabrication/cutList/${cutListId}/do`);
  const { apiCall: patchPropertiesApiCall } = useEvolvePatch('shop/fabrication/element/:elementId');

  return (
    <Card withBorder radius={5} p={0}>
      <Accordion
        styles={{
          item: { borderBottom: 0 },
        }}
      >
        <Accordion.Item value={cutListId}>
          <AccordionControlsWithActions>
            <Group>
              {cutList.name}
              {cutList.hasCutOptimizationErrors && (
                <InfoIcon
                  color="red"
                  label="This cut list could not be properly optimized. Expand the list to see the problematic elements."
                />
              )}
              {isNil(cutList.cutProfile) && canMarkItemsAsDone && <InfoIcon label="No cut profile assigned." />}
              <Badge color="gray.1" c="dark">
                <Group>
                  <SmallDetail label="Cuts">{cutList.cutCount}</SmallDetail>
                  <SmallDetail label="Stocks">{cutList.optimizedStockLengths}</SmallDetail>
                  <SmallDetail label="Used">
                    {/* Note: totalUsedStockLength is stored in feet. */}
                    {isNotNil(cutList.totalUsedStockLength)
                      ? convertInchesToImperialString(cutList.totalUsedStockLength * 12)
                      : null}
                  </SmallDetail>
                </Group>
              </Badge>
            </Group>
          </AccordionControlsWithActions>
          <Accordion.Panel>
            <Stack>
              <Group justify="space-between">
                <SearchableSelect
                  idKey="cutProfileId"
                  label="Cut Profile"
                  inlineLabel
                  placeholder="None"
                  paginatedGet={paginatedGetCutProfile}
                  value={cutList.cutProfile}
                  onChange={(cutProfile) => {
                    if (isNotNil(cutProfile)) {
                      void assignCutProfile(
                        {},
                        { url: `shop/fabrication/cutList/${cutListId}/change-cut-profile/${cutProfile.cutProfileId}` },
                      ).then(refreshCutList);
                    }
                  }}
                  getItemLabel={(cutProfile) => cutProfile.name}
                  size="xs"
                  disabled={!hasFabricationFullAccess}
                />
                <Group>
                  {actions}
                  {canMarkItemsAsDone && isNotNil(cutList.cutProfile) && (
                    <Button
                      variant="light"
                      size="xs"
                      leftSection={<EvolveIcon icon="Check" />}
                      onClick={() => {
                        void doCutList({}).then(refreshCutList);
                      }}
                    >
                      Cuts completed
                    </Button>
                  )}
                </Group>
              </Group>
              <AgGridEditable
                gridApi={gridApi}
                patchCall={patchPropertiesApiCall}
                configMapper={(element) => ({ url: `shop/fabrication/element/${element.elementId}` })}
                requestConverter={(_element, { customProperties: updatedCustomProperties }) => ({
                  customProperties: updatedCustomProperties,
                })}
              >
                {(applyWrap) => (
                  <AgGridReact<MachineElement>
                    onFirstDataRendered={applyWrap}
                    {...agGridProps}
                    domLayout="autoHeight"
                    rowHeight={compactRowHeight}
                    headerHeight={compactRowHeight}
                  />
                )}
              </AgGridEditable>
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>
        <LoadingOverlay visible={assigning || doing || loadingCutList} />
      </Accordion>

      <VariableTrackingModal
        title="Remove element from work order"
        variable={cutToRemove}
        onConfirm={async (item) => {
          if (isNotNil(item.workOrderItemId)) {
            await removeElementsApiCall(
              { elementIds: [item.elementId] },
              { url: `shop/workOrder/${cutList.workOrderId}/removeElements` },
            );
            await refreshCutList();
            refreshGrid(gridApi);
          }
        }}
        onClose={() => setCutToRemove(null)}
        confirmationText="Remove"
        buttonColor="red"
      >
        {(item) => (
          <Text>
            Are you sure you want to remove <b>{item.name}</b> from <b>{item.workOrderName}</b>?
          </Text>
        )}
      </VariableTrackingModal>
    </Card>
  );
};
